import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild, effect } from '@angular/core';
import { Router } from '@angular/router';
import { GoogleMap, MapAdvancedMarker, MapInfoWindow } from '@angular/google-maps';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { environment } from '../../../../environments/environment';
import { SiteService } from '../../../core/services/site.service';
import { TenantService } from '../../../core/services/tenant.service';
import { AuthService } from '../../../core/auth/auth.service';
import { NotificationService } from '../../../core/services/notification.service';
import { hasMinRole } from '../../../core/auth/role.model';
import { SiteListItem } from '../../../shared/interfaces';
import { isWinter } from '../../../shared/utils/season.util';
import {
  buildWindLegendItems,
  buildTemperatureLegendItems,
  buildWbgtLegendItems,
  getMarkerColor,
  getMarkerZIndex,
  getSeasonalStatusBadgeClass,
  getWindMarkerColor,
  getWindMarkerZIndex,
  getWindStatusBadgeClass
} from '../../../shared/utils/sensor-status';
import { getWindDirectionDegrees } from '../../../shared/utils/wind-direction';

type MapMode = 'temperature' | 'wind';

@Component({
  selector: 'app-site-map',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full overflow-hidden' },
  imports: [
    GoogleMap,
    MapAdvancedMarker,
    MapInfoWindow,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatButtonToggleModule
  ],
  templateUrl: './site-map.html',
  styleUrl: './site-map.scss',
})
export class SiteMapComponent {
  private readonly siteService = inject(SiteService);
  private readonly tenantService = inject(TenantService);
  private readonly authService = inject(AuthService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);

  public readonly isLoading = this.siteService.isLoading;
  public readonly error = this.siteService.error;

  /** 現在冬季かどうか */
  public readonly winterMode = isWinter();
  public readonly mapMode = signal<MapMode>('temperature');

  public readonly mapOptions: google.maps.MapOptions = {
    mapId: environment.googleMapsMapId,
    center: { lat: 34.91663711768134, lng: 135.5000950822929 }, // デフォルト: 大阪周辺
    zoom: 9,
    disableDefaultUI: false,
  };

  public readonly mapMarkers = computed(() => {
    const mode = this.mapMode();
    return this.siteService.siteListItems()
      .filter(site => site.geopoint && (mode === 'temperature' || site.windSpeedStatusNo !== undefined))
      .map(site => ({
        site,
        position: { lat: site.geopoint!.latitude, lng: site.geopoint!.longitude },
        pin: this.createPinElement(
          site.id,
          mode,
          mode === 'wind' ? site.windSpeedStatusNo : site.wbgtStatusNo,
          site.windDirectionCode,
        ),
        zIndex: mode === 'wind'
          ? getWindMarkerZIndex(site.windSpeedStatusNo)
          : getMarkerZIndex(site.wbgtStatusNo, this.winterMode),
      }));
  });

  public readonly googleMap = viewChild(GoogleMap);
  public readonly infoWindow = viewChild(MapInfoWindow);
  public readonly selectedSite = signal<SiteListItem | null>(null);

  public readonly canSaveMapSettings = computed(() => hasMinRole(this.authService.userRole(), 'org_admin'));

  public readonly legendItems = computed(() => {
    if (this.mapMode() === 'wind') {
      return buildWindLegendItems();
    }
    if (this.winterMode) {
      return buildTemperatureLegendItems(this.tenantService.organization()?.temperatureThresholds);
    }
    return buildWbgtLegendItems(this.tenantService.organization()?.wbgtThresholds);
  });

  public readonly legendTitle = computed(() => {
    if (this.mapMode() === 'wind') return '風速凡例';
    return this.winterMode ? '気温凡例' : 'WBGT凡例';
  });

  constructor() {
    // マップページを開いた時に常に最新のサイトデータを取得する
    this.siteService.reloadSites();
    let lastAppliedOrgId: string | undefined;
    effect(() => {
      const org = this.tenantService.organization();
      const map = this.googleMap()?.googleMap;
      
      if (!org || !map) return;

      if (org.id !== lastAppliedOrgId) {
        if (org.defaultMapCenter) {
          map.setCenter(org.defaultMapCenter);
          if (org.defaultMapZoom != null) {
            map.setZoom(org.defaultMapZoom);
          }
        }
        lastAppliedOrgId = org.id;
      }
    });
  }

  public async saveMapSettings(): Promise<void> {
    const map = this.googleMap()?.googleMap;
    if (!map) return;

    const center = map.getCenter();
    const zoom = map.getZoom();

    if (center && zoom !== undefined) {
      const lat = center.lat();
      const lng = center.lng();
      
      try {
        await this.tenantService.updateMapSettings({ lat, lng }, zoom);
        this.notificationService.success('マップの初期表示位置を保存しました');
      } catch (err) {
        this.notificationService.error('マップ設定の保存に失敗しました');
      }
    }
  }

  public openInfoWindow(marker: MapAdvancedMarker, site: SiteListItem) {
    this.selectedSite.set(site);
    this.infoWindow()?.open(marker);
  }

  public onMapModeChange(mode: MapMode) {
    this.mapMode.set(mode);
    this.selectedSite.set(null);
    this.infoWindow()?.close();
  }

  public navigateToDetail(siteId: string) {
    this.router.navigate(['/sites/detail', siteId]);
  }

  /** 選択中サイトのステータスバッジCSSクラスを返す */
  public getSelectedSiteStatusBadgeClass(): string {
    const site = this.selectedSite();
    if (!site) return '';
    if (this.mapMode() === 'wind') return getWindStatusBadgeClass(site.windSpeedStatusNo);
    return getSeasonalStatusBadgeClass(site.wbgtStatusNo, this.winterMode);
  }

  private readonly pinCache = new Map<string, HTMLElement>();
  private readonly sitePinCache = new Map<string, { key: string; element: HTMLElement }>();

  private createPinElement(siteId: string, mode: MapMode, statusNo: string | undefined, windDirectionCode?: number): HTMLElement {
    const key = mode === 'wind'
      ? `wind-${statusNo || 'default'}-${windDirectionCode ?? 'none'}`
      : `temperature-${this.winterMode ? 'winter' : 'summer'}-${statusNo || 'default'}`;
    const cached = this.sitePinCache.get(siteId);
    if (cached && cached.key === key) {
      return cached.element;
    }

    if (!this.pinCache.has(key)) {
      const pin = mode === 'wind'
        ? this.createWindPinElement(statusNo, windDirectionCode)
        : this.createTemperaturePinElement(statusNo);
      this.pinCache.set(key, pin);
    }

    const element = this.pinCache.get(key)!.cloneNode(true) as HTMLElement;
    this.sitePinCache.set(siteId, { key, element });
    return element;
  }

  private createTemperaturePinElement(statusNo: string | undefined): HTMLElement {
    const pin = document.createElement('div');
    pin.style.width = '24px';
    pin.style.height = '24px';
    pin.style.borderRadius = '50%';
    pin.style.border = '2px solid white';
    pin.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
    pin.style.cursor = 'pointer';
    pin.style.backgroundColor = getMarkerColor(statusNo, this.winterMode);
    return pin;
  }

  private createWindPinElement(statusNo: string | undefined, windDirectionCode?: number): HTMLElement {
    if (windDirectionCode === undefined) {
      const pin = document.createElement('div');
      pin.style.width = '24px';
      pin.style.height = '24px';
      pin.style.borderRadius = '50%';
      pin.style.border = '2px solid white';
      pin.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
      pin.style.cursor = 'pointer';
      pin.style.backgroundColor = getWindMarkerColor(statusNo);
      return pin;
    }

    const pin = document.createElement('div');
    pin.style.width = '32px';
    pin.style.height = '32px';
    pin.style.display = 'flex';
    pin.style.alignItems = 'center';
    pin.style.justifyContent = 'center';
    pin.style.filter = 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))';
    pin.style.cursor = 'pointer';
    pin.style.transform = `rotate(${getWindDirectionDegrees(windDirectionCode)}deg)`;
    pin.style.transformOrigin = 'center';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 32 32');
    svg.setAttribute('width', '32');
    svg.setAttribute('height', '32');
    svg.setAttribute('aria-hidden', 'true');

    const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    arrow.setAttribute('d', 'M16 5 24 26 16 21.5 8 26 16 5Z');
    arrow.setAttribute('fill', getWindMarkerColor(statusNo));
    arrow.setAttribute('stroke', '#ffffff');
    arrow.setAttribute('stroke-width', '2');
    arrow.setAttribute('stroke-linejoin', 'round');

    svg.append(arrow);
    pin.append(svg);
    return pin;
  }
}
